import type { Chord } from '@/types/music';

/**
 * Hand-authored chord library.
 *
 * `frets` and `fingers` run from the 6th string (low E) to the 1st string
 * (high E). `null` = do not play, `0` = open.
 *
 * Every voicing here was checked against standard tuning by resolving it back
 * to pitch classes (see `chords.test.ts`, which asserts that each fingering
 * really produces the notes its quality claims). The reference screenshots were
 * *not* treated as musical truth.
 */
export const CHORDS: Chord[] = [
  // ── Beginner open chords ────────────────────────────────────────────────
  {
    id: 'Em',
    name: 'Em',
    nameRu: 'Ми минор',
    root: 4,
    quality: 'minor',
    frets: [0, 2, 2, 0, 0, 0],
    fingers: [0, 2, 3, 0, 0, 0],
    baseFret: 1,
    category: 'beginner',
    difficulty: 1,
    tipRu: 'Самый лёгкий аккорд. Все шесть струн звучат — бей смело сверху донизу.',
  },
  {
    id: 'Am',
    name: 'Am',
    nameRu: 'Ля минор',
    root: 9,
    quality: 'minor',
    frets: [null, 0, 2, 2, 1, 0],
    fingers: [0, 0, 2, 3, 1, 0],
    baseFret: 1,
    category: 'beginner',
    difficulty: 1,
    tipRu: 'Начинай бой с пятой струны. Шестую не задевай — она здесь лишняя.',
  },
  {
    id: 'C',
    name: 'C',
    nameRu: 'До мажор',
    root: 0,
    quality: 'major',
    frets: [null, 3, 2, 0, 1, 0],
    fingers: [0, 3, 2, 0, 1, 0],
    baseFret: 1,
    category: 'beginner',
    difficulty: 2,
    tipRu: 'Ставь пальцы по диагонали. Первая струна должна звенеть — не клади на неё ладонь.',
  },
  {
    id: 'G',
    name: 'G',
    nameRu: 'Соль мажор',
    root: 7,
    quality: 'major',
    frets: [3, 2, 0, 0, 0, 3],
    fingers: [2, 1, 0, 0, 0, 3],
    baseFret: 1,
    category: 'beginner',
    difficulty: 2,
    tipRu: 'Пальцы расходятся широко. Держи запястье ниже грифа, тогда достанешь спокойно.',
  },
  {
    id: 'D',
    name: 'D',
    nameRu: 'Ре мажор',
    root: 2,
    quality: 'major',
    frets: [null, null, 0, 2, 3, 2],
    fingers: [0, 0, 0, 1, 3, 2],
    baseFret: 1,
    category: 'beginner',
    difficulty: 2,
    tipRu: 'Играются только четыре тонкие струны. Пальцы стоят треугольником.',
  },
  {
    id: 'Dm',
    name: 'Dm',
    nameRu: 'Ре минор',
    root: 2,
    quality: 'minor',
    frets: [null, null, 0, 2, 3, 1],
    fingers: [0, 0, 0, 2, 3, 1],
    baseFret: 1,
    category: 'beginner',
    difficulty: 2,
    tipRu: 'Тот же треугольник, что и в D, только сдвинут. Тоже четыре струны.',
  },
  {
    id: 'E',
    name: 'E',
    nameRu: 'Ми мажор',
    root: 4,
    quality: 'major',
    frets: [0, 2, 2, 1, 0, 0],
    fingers: [0, 2, 3, 1, 0, 0],
    baseFret: 1,
    category: 'beginner',
    difficulty: 1,
    tipRu: 'Это Em плюс один палец на третьей струне. Звучат все шесть струн.',
  },
  {
    id: 'A',
    name: 'A',
    nameRu: 'Ля мажор',
    root: 9,
    quality: 'major',
    frets: [null, 0, 2, 2, 2, 0],
    fingers: [0, 0, 1, 2, 3, 0],
    baseFret: 1,
    category: 'beginner',
    difficulty: 2,
    tipRu: 'Три пальца в один лад. Ставь их плотнее друг к другу, чтобы первая струна звенела.',
  },

  // ── Common chords ───────────────────────────────────────────────────────
  {
    id: 'F',
    name: 'F',
    nameRu: 'Фа мажор',
    root: 5,
    quality: 'major',
    frets: [1, 3, 3, 2, 1, 1],
    fingers: [1, 3, 4, 2, 1, 1],
    baseFret: 1,
    barre: { fret: 1, fromString: 1, toString: 6, finger: 1 },
    category: 'common',
    difficulty: 5,
    tipRu: 'Баррэ. Прижимай не подушечкой, а боковой стороной пальца — так жёстче и не больно.',
  },
  {
    id: 'F_easy',
    name: 'F',
    nameRu: 'Фа мажор (лёгкий)',
    root: 5,
    quality: 'major',
    frets: [null, null, 3, 2, 1, 1],
    fingers: [0, 0, 3, 2, 1, 1],
    baseFret: 1,
    barre: { fret: 1, fromString: 1, toString: 2, finger: 1 },
    category: 'common',
    difficulty: 3,
    beginnerVariantOf: 'F',
    tipRu: 'Пока баррэ не даётся — играй так. Четыре тонкие струны, звучит честно как F.',
  },
  {
    id: 'Bm',
    name: 'Bm',
    nameRu: 'Си минор',
    root: 11,
    quality: 'minor',
    frets: [null, 2, 4, 4, 3, 2],
    fingers: [0, 1, 3, 4, 2, 1],
    baseFret: 1,
    barre: { fret: 2, fromString: 1, toString: 5, finger: 1 },
    category: 'common',
    difficulty: 5,
    tipRu: 'Баррэ на втором ладу, шестая струна не играется.',
  },
  {
    id: 'B7',
    name: 'B7',
    nameRu: 'Си септаккорд',
    root: 11,
    quality: 'dominant7',
    frets: [null, 2, 1, 2, 0, 2],
    fingers: [0, 2, 1, 3, 0, 4],
    baseFret: 1,
    category: 'common',
    difficulty: 3,
    tipRu: 'Замена трудного Bm в песнях с Em. Вторая струна остаётся открытой.',
  },
  {
    id: 'A7',
    name: 'A7',
    nameRu: 'Ля септаккорд',
    root: 9,
    quality: 'dominant7',
    frets: [null, 0, 2, 0, 2, 0],
    // Index and ring, not middle and ring: A is played 1-2-3 on the D, G and B
    // strings, so lifting the middle finger leaves exactly this shape and the
    // A ↔ A7 change costs one finger instead of a re-grip.
    fingers: [0, 0, 1, 0, 3, 0],
    baseFret: 1,
    category: 'common',
    difficulty: 1,
    tipRu: 'Это A, из которого убрали один палец. Часто идёт перед D.',
  },
  {
    id: 'D7',
    name: 'D7',
    nameRu: 'Ре септаккорд',
    root: 2,
    quality: 'dominant7',
    frets: [null, null, 0, 2, 1, 2],
    fingers: [0, 0, 0, 2, 1, 3],
    baseFret: 1,
    category: 'common',
    difficulty: 2,
    tipRu: 'Тянет в G. Четыре тонкие струны, как у D.',
  },
  {
    id: 'E7',
    name: 'E7',
    nameRu: 'Ми септаккорд',
    root: 4,
    quality: 'dominant7',
    frets: [0, 2, 0, 1, 0, 0],
    fingers: [0, 2, 0, 1, 0, 0],
    baseFret: 1,
    category: 'common',
    difficulty: 1,
    tipRu: 'E, из которого убрали палец с четвёртой струны. Всего два пальца.',
  },
  {
    id: 'G7',
    name: 'G7',
    nameRu: 'Соль септаккорд',
    root: 7,
    quality: 'dominant7',
    frets: [3, 2, 0, 0, 0, 1],
    fingers: [3, 2, 0, 0, 0, 1],
    baseFret: 1,
    category: 'common',
    difficulty: 2,
    tipRu: 'Тот же G, но мизинец уходит на первый лад первой струны.',
  },
  {
    id: 'C7',
    name: 'C7',
    nameRu: 'До септаккорд',
    root: 0,
    quality: 'dominant7',
    frets: [null, 3, 2, 3, 1, 0],
    fingers: [0, 3, 2, 4, 1, 0],
    baseFret: 1,
    category: 'common',
    difficulty: 3,
    // Standard open C7 sounds C–E–B♭ and drops the fifth. That is the normal
    // guitar voicing: in a four-note chord the fifth is the omissible tone.
    tipRu: 'C плюс мизинец на третьей струне.',
  },

  // ── Useful extensions ───────────────────────────────────────────────────
  {
    id: 'Am7',
    name: 'Am7',
    nameRu: 'Ля минорный септаккорд',
    root: 9,
    quality: 'minor7',
    frets: [null, 0, 2, 0, 1, 0],
    fingers: [0, 0, 2, 0, 1, 0],
    baseFret: 1,
    category: 'extended',
    difficulty: 1,
    tipRu: 'Am без одного пальца. Мягче и печальнее, чем Am.',
  },
  {
    id: 'Em7',
    name: 'Em7',
    nameRu: 'Ми минорный септаккорд',
    root: 4,
    quality: 'minor7',
    frets: [0, 2, 0, 0, 0, 0],
    fingers: [0, 2, 0, 0, 0, 0],
    baseFret: 1,
    category: 'extended',
    difficulty: 1,
    tipRu: 'Один палец на всю гитару. Идеально, чтобы отдохнуть посреди песни.',
  },
  {
    id: 'Dm7',
    name: 'Dm7',
    nameRu: 'Ре минорный септаккорд',
    root: 2,
    quality: 'minor7',
    frets: [null, null, 0, 2, 1, 1],
    fingers: [0, 0, 0, 2, 1, 1],
    baseFret: 1,
    barre: { fret: 1, fromString: 1, toString: 2, finger: 1 },
    category: 'extended',
    difficulty: 3,
    tipRu: 'Указательный лежит сразу на двух тонких струнах — маленькое баррэ на тренировку.',
  },
  {
    id: 'Cmaj7',
    name: 'Cmaj7',
    nameRu: 'До большой септаккорд',
    root: 0,
    quality: 'major7',
    frets: [null, 3, 2, 0, 0, 0],
    fingers: [0, 3, 2, 0, 0, 0],
    baseFret: 1,
    category: 'extended',
    difficulty: 1,
    tipRu: 'C, из которого убрали палец со второй струны. Звучит мечтательно.',
  },
  {
    id: 'Fmaj7',
    name: 'Fmaj7',
    nameRu: 'Фа большой септаккорд',
    root: 5,
    quality: 'major7',
    frets: [null, null, 3, 2, 1, 0],
    fingers: [0, 0, 3, 2, 1, 0],
    baseFret: 1,
    category: 'extended',
    difficulty: 2,
    tipRu: 'Мягкая замена F, пока не встало баррэ. Играй только четыре тонкие струны.',
  },
  {
    id: 'Dsus2',
    name: 'Dsus2',
    nameRu: 'Ре с секундой',
    root: 2,
    quality: 'sus2',
    frets: [null, null, 0, 2, 3, 0],
    fingers: [0, 0, 0, 1, 3, 0],
    baseFret: 1,
    category: 'extended',
    difficulty: 2,
    tipRu: 'D, у которого сняли палец с первой струны. Звенит открыто.',
  },
  {
    id: 'Dsus4',
    name: 'Dsus4',
    nameRu: 'Ре с квартой',
    root: 2,
    quality: 'sus4',
    frets: [null, null, 0, 2, 3, 3],
    fingers: [0, 0, 0, 1, 3, 4],
    baseFret: 1,
    category: 'extended',
    difficulty: 2,
    tipRu: 'D плюс мизинец. Чередуй D → Dsus4 → D — это уже звучит как проигрыш.',
  },
  {
    id: 'Asus2',
    name: 'Asus2',
    nameRu: 'Ля с секундой',
    root: 9,
    quality: 'sus2',
    frets: [null, 0, 2, 2, 0, 0],
    fingers: [0, 0, 1, 2, 0, 0],
    baseFret: 1,
    category: 'extended',
    difficulty: 1,
    tipRu: 'A без третьего пальца. Два пальца, звучит просторно.',
  },
  {
    id: 'Asus4',
    name: 'Asus4',
    nameRu: 'Ля с квартой',
    root: 9,
    quality: 'sus4',
    frets: [null, 0, 2, 2, 3, 0],
    fingers: [0, 0, 1, 2, 3, 0],
    baseFret: 1,
    category: 'extended',
    difficulty: 2,
    tipRu: 'A, где третий палец переехал на лад выше. Просится обратно в A.',
  },
];

const CHORD_INDEX = new Map(CHORDS.map((c) => [c.id, c]));

export function getChord(id: string): Chord | undefined {
  return CHORD_INDEX.get(id);
}

/** Throws — use where a missing chord means a programming error. */
export function requireChord(id: string): Chord {
  const chord = CHORD_INDEX.get(id);
  if (!chord) throw new Error(`Unknown chord id: ${id}`);
  return chord;
}

export function allChordIds(): string[] {
  return CHORDS.map((c) => c.id);
}
